import { __spreadArray } from "tslib";
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { useState, useMemo, useCallback } from "react";
import { indexIncludes, indexEquals } from "./utils";
import createItemsTree from "./create-items-tree";
import moveHighlightOneStep from "./move-highlight";
export default function useHighlightedMenu(_a) {
    var items = _a.items, hasExpandableGroups = _a.hasExpandableGroups, _b = _a.isInRestrictedView, isInRestrictedView = _b === void 0 ? false : _b;
    var _c = useState([]), targetIndex = _c[0], setTargetIndex = _c[1];
    var _d = useState([]), expandedIndex = _d[0], setExpandedIndex = _d[1];
    var _e = useState(true), isUsingMouse = _e[0], setIsUsingMouse = _e[1];
    var _f = useMemo(function() {
        return createItemsTree(items);
    }, [
        items
    ]), getItem = _f.getItem, getItemIndex = _f.getItemIndex, getSequentialIndex = _f.getSequentialIndex, getParentIndex = _f.getParentIndex;
    var targetItem = useMemo(function() {
        return getItem(targetIndex);
    }, [
        targetIndex,
        getItem
    ]);
    var isHighlighted = useCallback(function(item) {
        var index = getItemIndex(item);
        return indexIncludes(index, targetIndex);
    }, [
        targetIndex,
        getItemIndex
    ]);
    // check if keyboard focus is on the element
    var isKeyboardHighlight = useCallback(function(item) {
        var index = getItemIndex(item);
        return !isUsingMouse && indexEquals(index, targetIndex);
    }, [
        targetIndex,
        getItemIndex,
        isUsingMouse
    ]);
    var isExpanded = useCallback(function(group) {
        var index = getItemIndex(group);
        return indexIncludes(index, expandedIndex);
    }, [
        expandedIndex,
        getItemIndex
    ]);
    var moveHighlight = useCallback(function(direction) {
        var getNext = function(index) {
            var nextIndex = getSequentialIndex(index, direction);
            var item = getItem(nextIndex || [
                -1
            ]);
            if (!nextIndex || !item) {
                return null;
            }
            var parentIndex = getParentIndex(item);
            var parentItem = parentIndex && getItem(parentIndex);
            return {
                index: nextIndex,
                item: item,
                parent: parentItem || undefined
            };
        };
        var nextIndex = moveHighlightOneStep({
            startIndex: targetIndex,
            expandedIndex: expandedIndex,
            getNext: getNext,
            hasExpandableGroups: hasExpandableGroups,
            isInRestrictedView: isInRestrictedView
        });
        if (nextIndex) {
            setTargetIndex(nextIndex);
        }
    }, [
        targetIndex,
        expandedIndex,
        getItem,
        getSequentialIndex,
        getParentIndex,
        hasExpandableGroups,
        isInRestrictedView
    ]);
    var highlightItem = useCallback(function(item) {
        setTargetIndex(getItemIndex(item));
    }, [
        getItemIndex
    ]);
    var expandGroup = useCallback(function(group) {
        var groupIndex = group ? getItemIndex(group) : targetIndex;
        var firstChildIndex = __spreadArray(__spreadArray([], groupIndex, true), [
            0
        ], false);
        // move to the first child item unless in restricted mode
        setTargetIndex(isInRestrictedView ? groupIndex : firstChildIndex);
        setExpandedIndex(groupIndex);
    }, [
        targetIndex,
        getItemIndex,
        isInRestrictedView
    ]);
    var collapseGroup = useCallback(function() {
        if (expandedIndex.length > 0) {
            setTargetIndex(expandedIndex);
            setExpandedIndex(expandedIndex.slice(0, -1));
        }
    }, [
        expandedIndex
    ]);
    var reset = useCallback(function() {
        setTargetIndex([]);
        setExpandedIndex([]);
    }, []);
    return {
        targetItem: targetItem,
        isHighlighted: isHighlighted,
        isKeyboardHighlight: isKeyboardHighlight,
        isExpanded: isExpanded,
        moveHighlight: moveHighlight,
        highlightItem: highlightItem,
        expandGroup: expandGroup,
        collapseGroup: collapseGroup,
        reset: reset,
        setIsUsingMouse: setIsUsingMouse
    };
} //# sourceMappingURL=use-highlighted-menu.js.map
